import { createContext, use } from 'react';

// Kept apart from the provider so its child surfaces never import the
// provider back (an import cycle).

export type OnboardingPath = 'trader' | 'staker';

export type RouteKey = 'markets' | 'community' | 'profile';

export type StepDef = {
  id: string;
  // The same data-onboarding attribute is set on the desktop and mobile
  // renderings of an anchor (header nav vs FooterMobile, table row vs card) —
  // both surfaces are conditionally rendered, so only one match exists.
  selector: string;
  route: RouteKey | null;
  title: string;
  body: string;
};

// `path` rides along for the coach-mark remount key — both paths end on a
// profile-pill step at the same index, so id+index alone would collide.
export type OnboardingStep = StepDef & {
  index: number;
  count: number;
  path: OnboardingPath;
};

export type OnboardingContextValue = {
  step: OnboardingStep | null;
  next: () => void;
  dismiss: () => void;
  // A page whose current step has no anchor ends the tour instead of
  // dangling on an invisible step.
  pathUnavailable: () => void;
  chooseTrader: () => void;
  chooseStaker: () => void;
  reopenWelcome: () => void;
};

export const OnboardingContext = createContext<OnboardingContextValue | null>(
  null,
);

export function useOnboarding(): OnboardingContextValue {
  const ctx = use(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return ctx;
}
